import React, { useState, useEffect } from 'react';
import { donorsService } from '../services/api';
import PhoneEditor from './PhoneEditor';
import AddressEditor from './AddressEditor';
import './DonorForm.css';

const DonorForm = ({ onSubmit, onCancel, initialData = null }) => {
  const [formData, setFormData] = useState(initialData || {
    first_name: '',
    last_name: '',
    email: '',
    phones: [],
    addresses: [],
    note: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Initialize form with donor data when editing
    if (initialData) {
      setFormData({
        first_name: initialData.first_name || initialData.first_name || '',
        last_name: initialData.last_name || initialData.last_name || '',
        email: initialData.email || '',
        phones: initialData.phones || [],
        addresses: initialData.addresses || [],
        note: ''
      });
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhonesChange = (phones) => {
    setFormData(prev => ({
      ...prev,
      phones
    }));
  };

  const handleAddressesChange = (addresses) => {
    setFormData(prev => ({
      ...prev,
      addresses
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (initialData?.id) {
        // Update existing donor
        await donorsService.update(initialData.id, formData);
      } else {
        // Create new donor
        const response = await donorsService.create(formData);
        onSubmit(response.data);
        return;
      }
      onSubmit(formData);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save donor');
      console.error('Error saving donor:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="form-header">
          <h2>{initialData ? 'Edit Donor' : 'Add New Donor'}</h2>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="form">
          {error && <div className="alert alert-error">{error}</div>}

          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name">First Name *</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                  placeholder="Jane"
                />
              </div>

              <div className="form-group">
                <label htmlFor="last_name">Last Name *</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                  placeholder="Smith"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="jane@example.com"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Contact Information</h3>
            
            <PhoneEditor
              phones={formData.phones}
              onChange={handlePhonesChange}
            />
            
            <AddressEditor
              addresses={formData.addresses}
              onChange={handleAddressesChange}
            />
          </div>

          {!initialData && <div className="form-section">
            <h3>Note</h3>
            
            <div className="form-group">
              <label htmlFor="note">Add a Note</label>
              <textarea
                id="note"
                name="note"
                value={formData.note}
                onChange={handleInputChange}
                placeholder="Add any additional notes about this donor"
                rows="3"
              />
            </div>
          </div>}

          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (initialData ? 'Update Donor' : 'Add Donor')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DonorForm;
